import { z } from "zod";
import { apiService } from "../api.service";
import {
	type GeocodeRequest,
	GeocodeRequestSchema,
	type GeocodeResult,
	GeocodeResultSchema,
	type GeocodeSuggestion,
	type GeocodeSuggestRequest,
	GeocodeSuggestRequestSchema,
	GeocodeSuggestResponseSchema,
} from "./geocode.types";
import { MapApiError } from "./map.service";

/**
 * Thrown when a geocode suggest or resolve request is rejected by the client-side rate limiter.
 */
export class GeocodeRateLimitError extends Error {
	constructor(message = "Geocode rate limit reached") {
		super(message);
		this.name = "GeocodeRateLimitError";
	}
}

export interface GeocodeRequestOptions {
	signal?: AbortSignal;
}

/**
 * Geocoding API client for address suggest and geocode endpoints.
 */
export class GeocodeService {
	/**
	 * POST /geocode/suggest — returns address suggestions without coordinates.
	 */
	async suggestAddress(
		request: GeocodeSuggestRequest,
	): Promise<GeocodeSuggestion[]> {
		const validated = GeocodeSuggestRequestSchema.parse(request);

		try {
			const response = await apiService.post<unknown>(
				"/geocode/suggest",
				validated,
			);
			const parsed = GeocodeSuggestResponseSchema.parse(response);

			if (!parsed.isSuccessStatusCode) {
				throw new MapApiError(
					"Geocode suggest request failed",
					parsed,
					parsed.statusCode,
				);
			}

			return parsed.response.suggestions.map((suggestion) => ({
				id: suggestion.magicKey,
				primary: suggestion.text,
				magicKey: suggestion.magicKey,
				isCollection: suggestion.isCollection,
			}));
		} catch (error) {
			if (error instanceof MapApiError || error instanceof z.ZodError) {
				throw error;
			}
			throw new MapApiError(
				`Failed to suggest addresses: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * POST /geocode — resolves an address or magicKey to coordinates.
	 */
	async geocode(
		request: GeocodeRequest,
		options?: GeocodeRequestOptions,
	): Promise<GeocodeResult> {
		const validated = GeocodeRequestSchema.parse(request);

		const hasMagicKey = Boolean(validated.magicKey?.trim());
		const hasAddress = validated.address != null;

		if (hasMagicKey === hasAddress) {
			throw new MapApiError(
				"Geocode request must include exactly one of magicKey or address",
			);
		}

		try {
			const response = await apiService.post<unknown>("/geocode", validated, {
				signal: options?.signal,
			});
			const parsed = GeocodeResultSchema.parse(response);

			if (!parsed.isSuccessStatusCode) {
				throw new MapApiError(
					"Geocode request failed",
					parsed,
					parsed.statusCode,
				);
			}

			return parsed;
		} catch (error) {
			if (error instanceof MapApiError || error instanceof z.ZodError) {
				throw error;
			}
			throw new MapApiError(
				`Failed to geocode address: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}
}

export const geocodeService = new GeocodeService();
